// Fixed assets — fixed_assets.* (Finance)
//
// The register of what the office owns and depreciates: the assets themselves,
// the monthly depreciation posting, and disposal.
//
// The ids below are the central key service's **approval numbers** (approved
// 21 Sep 2026), which the amer-laravel migration
// `2026_09_21_000002_add_finance_close_and_control_and_department_restore_permissions.php`
// writes as each permission row's own `permissions.id` — the two have to stay
// equal. That migration is no longer parked in
// `database/migrations/pending-central-approval/`; the keys were approved, so it
// sits in `database/migrations/` and runs with every other one.
//
// An id here that does not match its backend row fails silently:
// `HAS_PERMISSION` simply returns false, so the page, the sidebar leaf and every
// gated button never appear, with nothing logged anywhere. Read an issued id;
// never derive the next one from the last — post_depreciation and dispose were
// issued 1602/1603 while the register's own five landed at 1627-1631.

/** fixed_assets.index — the register, a row in it, and the depreciation runs. */
export const FIXED_ASSETS_INDEX = 1627;

/**
 * There is no `fixed_assets.show` key — the August 2026 audit collapsed detail
 * routes into their module's `index`, and the route file guards `GET /{id}`,
 * `GET /depreciation-runs` and `GET /depreciation-runs/{id}` with `index`
 * accordingly.
 *
 * Aliased rather than pointed at a key that does not exist: a constant naming a
 * missing key makes `HAS_PERMISSION` false forever and the record page vanishes
 * for everyone.
 */
export const FIXED_ASSETS_SHOW = FIXED_ASSETS_INDEX;

/** fixed_assets.create — filing an asset on the register. */
export const FIXED_ASSETS_CREATE = 1628;

/** fixed_assets.update — correcting a filed asset. */
export const FIXED_ASSETS_UPDATE = 1629;

/** fixed_assets.delete — only ever reachable while nothing has been charged. */
export const FIXED_ASSETS_DELETE = 1630;

/** fixed_assets.restore — bringing back an asset deleted by mistake. */
export const FIXED_ASSETS_RESTORE = 1631;

/**
 * fixed_assets.post_depreciation — charging the whole register one period.
 *
 * Its own key, not `create`: filing an asset and posting a month's depreciation
 * to the general ledger are different authorities, and only the second one
 * writes a journal.
 */
export const FIXED_ASSETS_POST_DEPRECIATION = 1602;

/**
 * fixed_assets.dispose — taking an asset off the register.
 *
 * Its own key, not `update`: it removes the cost and the accumulated
 * depreciation from the ledger and books a gain or a loss, which is not what the
 * edit form does.
 */
export const FIXED_ASSETS_DISPOSE = 1603;
